import React, { useEffect, useState } from "react";
import { ArrowLeftRight, Plus, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge } from "@/components/common";

const STATUS = { requested: ["Permintaan", "bg-sky-100 text-sky-700"], approved: ["Disetujui", "bg-amber-100 text-amber-700"], in_transit: ["Dikirim", "bg-amber-100 text-amber-700"], received: ["Diterima", "bg-emerald-100 text-emerald-700"], completed: ["Selesai", "bg-emerald-100 text-emerald-700"], rejected: ["Ditolak", "bg-rose-100 text-rose-700"] };

export default function Transfers() {
  const { branch, branches, can, isOwner } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/transfers", branch ? { branch_id: branch.id } : {})); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  const act = async (t, path, body) => {
    try {
      if (path === "receive") await api.post(`/transfers/${t.id}/receive`, { items: t.items.map((l) => ({ material_id: l.material_id, qty_received: l.qty })) });
      else await api.post(`/transfers/${t.id}/${path}`, body || {});
      toast.success("Berhasil"); load();
    } catch (e) { toast.error(e.message); }
  };
  const reject = async (t) => { const reason = window.prompt("Alasan tolak?"); if (!reason) return; act(t, "reject", { reason }); };
  const del = async (t) => {
    if (!window.confirm(`Hapus permanen transfer ${t.transfer_no}? Stok keluar/masuk akan dibalikkan. Tidak bisa dibatalkan.`)) return;
    try { await api.del(`/transfers/${t.id}`); toast.success("Transfer dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Transfer Stok Antar Cabang" subtitle={branch?.name} icon={ArrowLeftRight}
        actions={can("stock_transfer", "create") && <Button onClick={() => setCreating(true)} data-testid="add-transfer-btn"><Plus size={16} /> Minta Transfer</Button>} />

      {loading ? <Loading /> : !list.length ? <EmptyState icon={ArrowLeftRight} title="Belum ada transfer" /> : (
        <div className="space-y-2">
          {list.map((t) => {
            const [label, cls] = STATUS[t.status] || ["-", ""];
            return (
              <Card key={t.id} className="p-4" data-testid={`transfer-${t.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-slate-800">{t.transfer_no}</div>
                    <div className="text-xs text-slate-400">{t.from_branch_name} → {t.to_branch_name} · {t.items.length} item · {fmtDateTime(t.created_at)}</div>
                    <div className="text-xs text-slate-500 mt-1">{t.items.map((l) => `${l.material_name} ×${num(l.qty)}`).join(", ")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cls}>{label}</Badge>
                    {t.status === "requested" && can("stock_transfer", "approve") && <><Button size="sm" onClick={() => act(t, "approve")}>Setujui</Button><Button size="sm" variant="ghost" onClick={() => reject(t)}>Tolak</Button></>}
                    {t.status === "approved" && can("stock_transfer", "edit") && <Button size="sm" onClick={() => act(t, "send")}>Kirim</Button>}
                    {t.status === "in_transit" && <Button size="sm" onClick={() => act(t, "receive")}>Terima</Button>}
                    {isOwner && <button onClick={() => del(t)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-transfer-${t.id}`} title="Hapus (Owner)"><Trash2 size={15} /></button>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && <TransferCreate branches={branches} defaultTo={branch?.id} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </>
  );
}

function TransferCreate({ branches, defaultTo, onClose, onSaved }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(defaultTo || "");
  const [materials, setMaterials] = useState([]);
  const [lines, setLines] = useState([{ material_id: "", qty: 1 }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/materials").then(setMaterials); }, []);

  const save = async () => {
    if (!from || !to || from === to) return toast.error("Pilih cabang asal & tujuan berbeda");
    const items = lines.filter((l) => l.material_id).map((l) => ({ material_id: l.material_id, qty: Number(l.qty) }));
    if (!items.length) return toast.error("Tambah item");
    setBusy(true);
    try { await api.post("/transfers", { from_branch_id: from, to_branch_id: to, items }); toast.success("Permintaan transfer dibuat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Minta Transfer Stok" maxWidth="max-w-md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Dari</span>
            <select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="">Pilih...</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Ke</span>
            <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="">Pilih...</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium text-slate-600">Item</span><Button size="sm" variant="subtle" onClick={() => setLines((l) => [...l, { material_id: "", qty: 1 }])}><Plus size={14} /></Button></div>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select value={l.material_id} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, material_id: e.target.value } : x))} className="flex-1 h-10 px-3 rounded-xl border border-slate-300 text-sm"><option value="">Pilih bahan...</option>{materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
              <input type="number" value={l.qty} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} className="w-20 h-10 px-2 rounded-xl border border-slate-300" />
              <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="w-9 text-rose-500"><X size={16} /></button>
            </div>
          ))}
        </div>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="tf-save">{busy ? "Menyimpan..." : "Kirim Permintaan"}</Button>
      </div>
    </Modal>
  );
}
